/**
 * CpaleadIntegrationPage — connection form + offer-mapping form + sync-status
 * panel for the CPALead ingest partner. Light theme, GREEN accent only.
 *
 * Polymorphic copy rule: the exact partner name "CPALead" appears once, in the
 * page title. Everywhere else uses the neutral label "Partner Network".
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	isValidApiKey,
	isValidOfferId,
	isValidPublisherId,
	type CredentialRow,
} from "./api";
import {
	useCpaleadCredentialCreate,
	useCpaleadCredentialsList,
	useCpaleadOfferCreate,
	useCpaleadOffersList,
	useCpaleadPostbacksList,
	useCpaleadSyncStatus,
} from "./hooks";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const INPUT_INVALID =
	"w-full rounded-[6px] border border-red-400 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";

function formatTimestamp(epochSec: number): string {
	if (!epochSec || Number.isNaN(epochSec)) return "never";
	const d = new Date(epochSec * 1000);
	if (Number.isNaN(d.getTime())) return "never";
	return d.toISOString().replace("T","").slice(0, 19) +" UTC";
}

function lastSyncFromCreds(items: CredentialRow[] | undefined): number {
	if (!items || items.length === 0) return 0;
	return items.reduce((max, r) => (r.created_at > max ? r.created_at : max), 0);
}

export function CpaleadIntegrationPage() {
	const credsQ = useCpaleadCredentialsList();
	const offersQ = useCpaleadOffersList();
	const postbacksQ = useCpaleadPostbacksList();
	const statusQ = useCpaleadSyncStatus();
	const createCredMut = useCpaleadCredentialCreate();
	const createOfferMut = useCpaleadOfferCreate();

	const [publisherId, setPublisherId] = useState("");
	const [apiKey, setApiKey] = useState("");
	const [credSaved, setCredSaved] = useState(false);

	const [cpaleadOfferId, setCpaleadOfferId] = useState("");
	const [trackerityOfferId, setTrackerityOfferId] = useState("");
	const [offerSaved, setOfferSaved] = useState(false);

	const publisherIdValid = publisherId.length === 0 || isValidPublisherId(publisherId);
	const apiKeyValid = apiKey.length === 0 || isValidApiKey(apiKey);
	const cpaleadOfferValid =
 cpaleadOfferId.length === 0 || isValidOfferId(cpaleadOfferId);
	const trackerityOfferValid =
 trackerityOfferId.length === 0 || isValidOfferId(trackerityOfferId);

	const canSubmitCred =
 publisherId.length > 0 &&
 apiKey.length > 0 &&
 publisherIdValid &&
 apiKeyValid &&
 !createCredMut.isPending;

	const canSubmitOffer =
 cpaleadOfferId.length > 0 &&
 trackerityOfferId.length > 0 &&
 cpaleadOfferValid &&
 trackerityOfferValid &&
 !createOfferMut.isPending;

	const handleCredSubmit = (e: FormEvent) => {
 e.preventDefault();
 setCredSaved(false);
 if (!canSubmitCred) return;
 createCredMut.mutate(
 {
 cpalead_publisher_id: publisherId.trim(),
 cpalead_api_key: apiKey.trim(),
 },
 {
 onSuccess: () => {
 setCredSaved(true);
 setApiKey("");
 },
 },
 );
	};

	const handleOfferSubmit = (e: FormEvent) => {
 e.preventDefault();
 setOfferSaved(false);
 if (!canSubmitOffer) return;
 createOfferMut.mutate(
 {
 cpalead_offer_id: cpaleadOfferId.trim(),
 trackerity_offer_id: trackerityOfferId.trim(),
 },
 {
 onSuccess: () => {
 setOfferSaved(true);
 setCpaleadOfferId("");
 setTrackerityOfferId("");
 },
 },
 );
	};

	const lastSyncEpoch = useMemo(
 () => lastSyncFromCreds(credsQ.data?.items),
 [credsQ.data],
	);

	const connected = Boolean(statusQ.data?.has_credentials);
	const hasPostbacks = Boolean(statusQ.data?.has_postbacks);
	const postbackCount = postbacksQ.data?.items.length ?? 0;
	const offerCount = offersQ.data?.items.length ?? 0;

	return (
 <main
 data-testid="cpalead-integration-page"
 className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <h1 className="text-green-700 font-semibold text-3xl font-semibold">
 CPALead Integration
 </h1>
 <p className="text-sm text-zinc-600">
 Connect your Partner Network account to ingest conversion postbacks
 and map offers to Trackerity.
 </p>
 </header>

 {/* Sync status panel */}
 <section
 className={CARD}
 data-testid="cpalead-integration-status"
 aria-label="sync status"
 >
 <div className="flex flex-col gap-3">
 <div className="flex items-center justify-between">
 <h2 className="text-green-700 font-semibold text-sm font-semibold text-zinc-800">
 Partner Network Status
 </h2>
 <span
 className={
 connected
 ? "rounded-full border border-green-300 bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-800"
 : "rounded-full border border-zinc-300 bg-zinc-50 px-2.5 py-0.5 text-xs font-medium text-zinc-700"
 }
 data-testid="cpalead-integration-status-badge"
 >
 {statusQ.isLoading
 ? "checking"
 : connected
 ? "Connected"
 : "Not connected"}
 </span>
 </div>
 <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
 <div>
 <dt className="text-xs uppercase tracking-wide text-zinc-500">
 Credentials
 </dt>
 <dd
 className="text-sm text-zinc-900"
 data-testid="cpalead-integration-status-creds"
 >
 {statusQ.data?.has_credentials ? "Configured" : "Missing"}
 </dd>
 </div>
 <div>
 <dt className="text-xs uppercase tracking-wide text-zinc-500">
 Offer Mappings
 </dt>
 <dd
 className="text-sm text-zinc-900"
 data-testid="cpalead-integration-status-offers"
 >
 {statusQ.data?.has_offer_mappings
 ? offerCount > 0
 ? `${offerCount} configured`
 : "Configured"
 : "None"}
 </dd>
 </div>
 <div>
 <dt className="text-xs uppercase tracking-wide text-zinc-500">
 Recent Postbacks
 </dt>
 <dd
 className="text-sm text-zinc-900"
 data-testid="cpalead-integration-status-postbacks"
 >
 {hasPostbacks
 ? postbackCount > 0
 ? `${postbackCount} received`
 : "Receiving"
 : "None yet"}
 </dd>
 </div>
 </dl>
 <div className="border-t border-zinc-100 pt-3">
 <span className="text-xs uppercase tracking-wide text-zinc-500">
 Last Sync
 </span>
 <p
 className="text-sm text-zinc-900"
 data-testid="cpalead-integration-last-sync"
 >
 {credsQ.isLoading
 ? "loading"
 : lastSyncEpoch
 ? formatTimestamp(lastSyncEpoch)
 : "never"}
 </p>
 </div>
 {statusQ.isLoading ? (
 <p
 className="text-xs text-zinc-500"
 data-testid="cpalead-integration-loading"
 role="status"
 >
 Loading status.
 </p>
 ) : null}
 </div>
 </section>

 {/* Connection form — POST /credentials */}
 <form
 onSubmit={handleCredSubmit}
 className={`${CARD} flex flex-col gap-4`}
 data-testid="cpalead-integration-form"
 aria-label="partner network connection form"
 >
 <div className="flex flex-col gap-1">
 <h2 className="text-green-700 font-semibold text-sm font-semibold text-zinc-800">
 Add Partner Network Credentials
 </h2>
 <p className="text-xs text-zinc-500">
 Your API key is hashed before storage. Only the first few
 characters are retained for display.
 </p>
 </div>

 <div className="flex flex-col gap-1.5">
 <label htmlFor="cpalead-integration-publisher-input" className={LABEL}>
 Publisher ID
 </label>
 <input
 id="cpalead-integration-publisher-input"
 type="text"
 required
 className={publisherIdValid ? INPUT : INPUT_INVALID}
 value={publisherId}
 autoComplete="off"
 onChange={(e) => setPublisherId(e.currentTarget.value)}
 data-testid="cpalead-integration-publisher-input"
 aria-invalid={!publisherIdValid}
 />
 {!publisherIdValid ? (
 <p
 className="text-xs text-red-600"
 role="alert"
 data-testid="cpalead-integration-publisher-error"
 >
 Publisher ID must be alphanumeric (dash / underscore allowed).
 </p>
 ) : null}
 </div>

 <div className="flex flex-col gap-1.5">
 <label htmlFor="cpalead-integration-key-input" className={LABEL}>
 API Key
 </label>
 <input
 id="cpalead-integration-key-input"
 type="password"
 required
 className={apiKeyValid ? INPUT : INPUT_INVALID}
 value={apiKey}
 autoComplete="off"
 onChange={(e) => setApiKey(e.currentTarget.value)}
 data-testid="cpalead-integration-key-input"
 aria-invalid={!apiKeyValid}
 />
 {!apiKeyValid ? (
 <p
 className="text-xs text-red-600"
 role="alert"
 data-testid="cpalead-integration-key-error"
 >
 API key must be at least 8 characters, alphanumeric only.
 </p>
 ) : (
 <p className="text-xs text-zinc-500">
 At least 8 characters. Alphanumeric, dash, underscore.
 </p>
 )}
 </div>

 <div className="flex items-center gap-3">
 <button
 type="submit"
 className={BTN}
 disabled={!canSubmitCred}
 data-testid="cpalead-integration-save"
 >
 {createCredMut.isPending ? "Saving." : "Connect Partner Network"}
 </button>
 {credSaved ? (
 <p
 className="text-sm text-green-700"
 role="status"
 data-testid="cpalead-integration-saved"
 >
 Credentials saved.
 </p>
 ) : null}
 {createCredMut.error ? (
 <p
 className="text-sm text-red-600"
 role="alert"
 data-testid="cpalead-integration-error"
 >
 {createCredMut.error.message}
 </p>
 ) : null}
 </div>
 </form>

 {/* Offer mapping form — POST /offers */}
 <form
 onSubmit={handleOfferSubmit}
 className={`${CARD} flex flex-col gap-4`}
 data-testid="cpalead-integration-offer-form"
 aria-label="offer mapping form"
 >
 <div className="flex flex-col gap-1">
 <h2 className="text-green-700 font-semibold text-sm font-semibold text-zinc-800">
 Map an Offer
 </h2>
 <p className="text-xs text-zinc-500">
 Link a Partner Network offer ID to the matching Trackerity offer.
 </p>
 </div>

 <div className="flex flex-col gap-1.5">
 <label htmlFor="cpalead-integration-offer-input" className={LABEL}>
 Partner Offer ID
 </label>
 <input
 id="cpalead-integration-offer-input"
 type="text"
 required
 className={cpaleadOfferValid ? INPUT : INPUT_INVALID}
 value={cpaleadOfferId}
 autoComplete="off"
 onChange={(e) => setCpaleadOfferId(e.currentTarget.value)}
 data-testid="cpalead-integration-offer-input"
 aria-invalid={!cpaleadOfferValid}
 />
 {!cpaleadOfferValid ? (
 <p
 className="text-xs text-red-600"
 role="alert"
 data-testid="cpalead-integration-offer-error"
 >
 Offer ID must be alphanumeric (dash / underscore allowed).
 </p>
 ) : null}
 </div>

 <div className="flex flex-col gap-1.5">
 <label htmlFor="cpalead-integration-trackerity-input" className={LABEL}>
 Trackerity Offer ID
 </label>
 <input
 id="cpalead-integration-trackerity-input"
 type="text"
 required
 className={trackerityOfferValid ? INPUT : INPUT_INVALID}
 value={trackerityOfferId}
 autoComplete="off"
 onChange={(e) => setTrackerityOfferId(e.currentTarget.value)}
 data-testid="cpalead-integration-trackerity-input"
 aria-invalid={!trackerityOfferValid}
 />
 {!trackerityOfferValid ? (
 <p
 className="text-xs text-red-600"
 role="alert"
 data-testid="cpalead-integration-trackerity-error"
 >
 Trackerity offer ID must be alphanumeric (dash / underscore allowed).
 </p>
 ) : null}
 </div>

 <div className="flex items-center gap-3">
 <button
 type="submit"
 className={BTN}
 disabled={!canSubmitOffer}
 data-testid="cpalead-integration-offer-save"
 >
 {createOfferMut.isPending ? "Saving." : "Map Offer"}
 </button>
 {offerSaved ? (
 <p
 className="text-sm text-green-700"
 role="status"
 data-testid="cpalead-integration-offer-saved"
 >
 Offer mapped.
 </p>
 ) : null}
 {createOfferMut.error ? (
 <p
 className="text-sm text-red-600"
 role="alert"
 data-testid="cpalead-integration-offer-error-msg"
 >
 {createOfferMut.error.message}
 </p>
 ) : null}
 </div>
 </form>
 </main>
	);
}

export default CpaleadIntegrationPage;
